export enum PowerDieColor {
    RED = 'Red',
    BLACK = 'Black',
    WHITE = 'White',
    MORTAL = 'Mortal',
}

export enum ArmoryItemType {
    TITAN = 'Titan',
    KRATOS_TABLE = 'Kratos Table',
    WEAPON = 'Weapon',
    GEAR = 'Gear',
    MNEMOS = 'Mnemos',
}

export type ArmoryItemSource = 'bundled' | 'custom';

export interface PrimordialStats {
    toHitValue: number;
    aeonTrespassValue: number;
    aeonTrespassValues?: number[];
}

export enum PowerReRollStrategy {
    LEGACY_HEURISTIC = 'Legacy Heuristic',
    TABLE_HEURISTIC = 'Table Heuristic',
    OPTIMAL_STRATEGY = 'Optimal Strategy',
}

export interface SimulationConfig {
    powerReRollThreshold: number;
    powerReRollStrategy: PowerReRollStrategy;
    numberOfSimulations: number;
    skipAttackRoll: boolean;
    disableAttackReRolls: boolean;
    disablePowerReRolls: boolean;
    disablePowerDiceLimits: boolean;
    autoIncrementRage: boolean;
}

export type ArmoryLoadout = NamedEntity & {
    argonaut: Argonaut;
    titan: Titan;
    kratosTable: KratosTable;
    weapon: Weapon;
    gear: Gear[];
    mnemos: Mnemos[];
};

export interface SimulationInput {
    loadouts: LoadoutStats[];
    primordial: PrimordialStats;
    kratosPool: KratosTokens;
    config: SimulationConfig;
}

export interface SimulationRunOptions {
    signal?: AbortSignal;
}

export type KratosTokenKey = keyof KratosTokens;
export type SimulationMode = 'standard' | 'compare';

export type BattlePressureLevel = 'I' | 'II' | 'III';

export interface WeaponMatrixTarget {
    bpLevel: BattlePressureLevel;
    aeonTrespassValue: number;
    aeonTrespassValues?: number[];
}

export interface WeaponMatrixScenario {
    id: string;
    label: string;
    groupLabel?: string;
    kratosPool: KratosTokens;
    loadouts: LoadoutStats[];
}

export interface WeaponMatrixInput {
    primordialName: string;
    primordialLevel: number;
    toHitValue: number;
    config: SimulationConfig;
    targets: WeaponMatrixTarget[];
    scenarios: WeaponMatrixScenario[];
}

export interface WeaponCompareSettings {
    primordialName: string;
    primordialLevel: number;
    toHitValue: number;
    targets: WeaponMatrixTarget[];
    tokenDepth: number;
    tokens: KratosTokenKey[];
    hasAllBPData: boolean;
}

export interface SimulationLoadoutStatsOutput {
    loadout: LoadoutStats;
    chanceToFullMiss: number;
    chanceToHit: number;
    chanceToFullHit: number;
    chanceToWound: number;
    diceRolledOnFullHit: PowerDieColor[];
    power: number[];
    powerCumulativeProbabilities: number[];
}

export type SimulationOutput = {
    primordial: PrimordialStats;
    loadoutOutputs: SimulationLoadoutStatsOutput[];
};

export interface WeaponMatrixCellOutput {
    scenarioId: string;
    bpLevel: BattlePressureLevel;
    kratosPool: KratosTokens;
    loadoutOutputs: SimulationLoadoutStatsOutput[];
}

export interface WeaponMatrixOutput {
    primordialName: string;
    primordialLevel: number;
    toHitValue: number;
    targets: WeaponMatrixTarget[];
    scenarios: WeaponMatrixScenario[];
    cells: WeaponMatrixCellOutput[];
}

export interface PowerRollInput {
    kratosPool: KratosTokens;
    hits: number;
    aeonTrespassValue: number;
    reRollThreshold: number;
    reRollStrategy?: PowerReRollStrategy;
    stats: LoadoutStats;
    disableReRolls: boolean;
    disableDiceLimits: boolean;
}

export interface AttackRollInput {
    kratosPool: KratosTokens;
    toHitValue: number;
    stats: LoadoutStats;
    disableReRolls: boolean;
}

export interface PowerDieFace {
    power: number;
    potential: number;
    dot: number;
}
export interface PowerDie {
    faces: PowerDieFace[];
}
export type RolledPowerDie = PowerDie & {
    currentFace: PowerDieFace;
};

export interface PowerDieCount {
    red: number;
    black: number;
    white: number;
    mortal: number;
}

export interface KratosTokens {
    break: number;
    black: number;
    fire: number;
    hope: number;
    opening: number;
    closing: number;
    rouse: number;
}

/** Argonaut-carried tokens that gate gear abilities */
export interface ArgonautTokens {
    ambrosia: number; // Cycle I
    despair: number; // Cycle I
    bleed: number; // Cycle III
    midas: number; // Cycle IV
    pain: number; // Cycle V
}

export const EMPTY_ARGONAUT_TOKENS: ArgonautTokens = {
    ambrosia: 0,
    despair: 0,
    bleed: 0,
    midas: 0,
    pain: 0,
};

export interface TriskellionStats {
    rage: number;
    fate: number;
    danger: number;
}

export type LoadoutStats = NamedEntity & {
    attackDice: number;
    attackReRolls: number;
    autoHits: number;
    autoTokens: KratosTokens;
    maxHits: number;
    overridePowerDice: boolean;
    perHitPowerDiceOverride: PowerDieColor[][];
    perHitPowerDice: PowerDieColor[];
    powerBonus: number;
    powerDice: PowerDieColor[];
    powerReRolls: number;
    precisionBonus: number;
};

export interface LoadoutStatRequirement {
    minTriskellion: TriskellionStats;
    maxTriskellion: TriskellionStats;
    minArgonautTokens?: Partial<ArgonautTokens>;
}
export type LoadoutStatsWithRequirements = LoadoutStats & {
    requirements: LoadoutStatRequirement;
};

export interface TaggedEntity {
    tags: string[];
}

export interface NamedEntity {
    name: string;
}

export type ArmoryItem = NamedEntity &
    TaggedEntity & {
        id?: string;
        source?: ArmoryItemSource;
        bundledOriginId?: string;
        bundledOriginName?: string;
        stats: LoadoutStatsWithRequirements[];
    };

/**
 * TODO - implement more robust gated abilities
 */
// export enum GateType {
//     RAGE = 'Rage',
//     FATE = 'Fate',
//     DANGER = 'Danger',
//     AMBROSIA = 'Ambrosia',
// }
// export enum Comparator {
//     AT_LEAST = 'At Least',
//     AT_MOST = 'At Most',
//     EQUALS = 'Equals',
// }
// export interface Gate {
//     type: GateType;
//     comparator: Comparator;
//     value: number;
// }

export type Argonaut = NamedEntity & TriskellionStats & ArgonautTokens & LoadoutStats;
export type Titan = ArmoryItem;
export type KratosTable = ArmoryItem;
export type Gear = ArmoryItem;
export type Mnemos = ArmoryItem;
export type Weapon = ArmoryItem;

export interface StoredArmoryLoadout {
    name: string;
    argonaut: Argonaut;
    titanId: string;
    kratosTableId: string;
    weaponId: string;
    gearIds: string[];
    mnemosIds: string[];
}

export interface PowerDieResult {
    die: RolledPowerDie;
    calculatedPower: number;
    usedTokens: KratosTokens;
}
export interface PowerRollResult {
    dieResults: PowerDieResult[];
    remainingTokens: KratosTokens;
}
